import React from "react";
import "./CustomSnack.css";

interface AlertState {
  open: boolean;
  severity: "success" | "error" | "warning" | "info";
  message: React.ReactNode;
  autoHideDuration: number;
}

type ShowAlertFunction = (
  severity: "success" | "error" | "warning" | "info",
  message: React.ReactNode,
  duration?: number
) => void;

interface CustomSnackBoxProps {
  children: (showAlert: ShowAlertFunction) => React.ReactNode;
}

export const CustomSnackBox: React.FC<CustomSnackBoxProps> = ({ children }) => {
  const [alertState, setAlertState] = React.useState<AlertState>({
    open: false,
    severity: "success",
    message: "",
    autoHideDuration: 1000,
  });

  const [timeoutId, setTimeoutId] = React.useState<NodeJS.Timeout | null>(null);

  const showAlert: ShowAlertFunction = (
    severity,
    message,
    duration = 1000
  ) => {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // First close any existing alert
    setAlertState(prev => ({ ...prev, open: false }));

    // Use setTimeout to ensure state update and re-render
    setTimeout(() => {
      setAlertState({
        open: true,
        severity,
        message,
        autoHideDuration: duration,
      });

      // Set new timeout for auto-hide
      const newTimeoutId = setTimeout(() => {
        setAlertState((prev) => ({ ...prev, open: false }));
        setTimeoutId(null);
      }, duration);

      setTimeoutId(newTimeoutId);
    }, 10); // Small delay to ensure state reset
  };

  const handleClose = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setAlertState((prev) => ({ ...prev, open: false }));
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return (
    <>
      {children(showAlert)}

      {alertState.open && (
        <div className={`custom-snackbar ${alertState.severity}`}>
          <span>{alertState.message}</span>
          <button
            className="close-btn"
            onClick={handleClose}
          >
            ✖
          </button>
        </div>
      )}
    </>
  );
};